import { useMemo, useState } from 'react'
import { Button, DatePicker, Divider, Input, Select, Space } from 'antd'
import moment from 'moment'

import { Queries, useApi } from 'admin/lib/Api'
import { ResponsiveWrapper } from 'admin/lib/Common/StyledElements'
import { FormItemWrapper } from 'admin/lib/Forms'
import { areAllAvailableSelected } from 'admin/lib/Functions/arrayObjectComparisonHelper'
import { Refresh } from 'admin/lib/Icons/Refresh'
import { Layout } from 'admin/lib/Layouts'
import { GroupUpdateTable } from './GroupUpdateTable'
import { UpdateRecordsSummaryModal } from './UpdateRecordSummaryModal'
import { addSpanToWhitespace } from 'admin/lib/Functions/whiteSpaceHelper'
import { updateableFilterOptions } from 'admin/lib/GroupUpdateHelpers/filterOptions'
import { UpdateRecordsModal } from './UpdateRecordsModal'

export function GroupUpdate() {
  const [selectedRecordIds, setSelectedRecordIds] = useState([])
  const [selectedValues, setSelectedValues] = useState([])

  const [fieldName, setFieldName] = useState()
  const [moduleName, setModuleName] = useState()
  const [hint, setHint] = useState()
  const [startDate, setStartDate] = useState(
    moment().subtract(1, 'year').startOf('day')
  )
  const [endDate, setEndDate] = useState(moment().endOf('day'))
  const [accountIds, setAccountIds] = useState([])
  const [accountsSearchInput, setAccountsSearchInput] = useState('')

  const [projectIds, setProjectIds] = useState([])
  const [projectsSearchInput, setProjectsSearchInput] = useState('')

  const [tableProps, setTableProps] = useState({
    fieldName: fieldName,
    moduleName: moduleName,
    hint: hint,
    startDate: startDate,
    endDate: endDate,
    accountIds: accountIds,
    projectIds: projectIds
  })

  const [showUpdateRecordsModal, setShowUpdateRecordsModal] = useState(false)
  const [showSummaryModal, setShowSummaryModal] = useState(false)

  const [submitEnabled, setSubmitEnabled] = useState(false)
  const [processing, setProcessing] = useState(false)
  const [updateRecords, setUpdateRecords] = useState([])
  const [updateRecordsValue, setUpdateRecordsValue] = useState(null)
  const [lastUpdatedValue, setLastUpdatedValue] = useState(null)

  const { RangePicker } = DatePicker
  const accountMappings = Queries.useUserSelfAccountMappings()

  const handleRunQuery = () => {
    setTableProps({
      fieldName: fieldName,
      moduleName: moduleName,
      hint: hint,
      startDate: startDate,
      endDate: endDate,
      accountIds: accountIds,
      projectIds: projectIds
    })
    setSelectedRecordIds([])
    setSelectedValues([])
  }

  const handleRunNewQuery = () => {
    handleRunQuery()
    localStorage.removeItem('customSort')
  }

  const accounts = useMemo(() => {
    const accs = {}
    if (accountMappings.data) {
      accountMappings.data.forEach(a => {
        const account_projects = []
        a.projects.map(p => account_projects.push(p.projectId))
        accs[a.accountId] = { name: a.name, projects: account_projects }
      })
    }
    return accs
  }, [accountMappings.data])

  const projects = useMemo(() => {
    const prjs = {}
    if (accountMappings.data) {
      accountMappings.data.map(a =>
        a.projects.map(p => (prjs[p.projectId] = p.name))
      )
    }
    return prjs
  }, [accountMappings.data])

  const availableAccounts = useMemo(() => {
    const filtered = {}
    Object.entries(accounts).forEach(([k, v]) => {
      if (
        v.name.toLowerCase().indexOf(accountsSearchInput.toLowerCase()) >= 0
      ) {
        filtered[k] = v
      }
    })

    return filtered
  }, [accounts, accountsSearchInput])

  const availableProjects = useMemo(() => {
    let available = {}
    if (accountIds.length == 0 || accountIds.length == accounts.length) {
      available = projects
    } else {
      accountIds.map(a =>
        accounts[a].projects.map(p => (available[p] = projects[p]))
      )
    }
    const filtered = {}
    Object.entries(available).forEach(([k, v]) => {
      if (v.toLowerCase().indexOf(projectsSearchInput.toLowerCase()) >= 0) {
        filtered[k] = v
      }
    })
    return filtered
  }, [accountIds, accounts, projects, projectsSearchInput])

  const onDateRangeChange = dates => {
    setStartDate(dates[0].local().format())
    setEndDate(dates[1].local().format())
  }

  const handleAccountIdsChange = value => {
    if (areAllAvailableSelected(value, availableAccounts)) {
      setAccountsSearchInput('')
    }

    setAccountIds(value)
  }

  const handleSelectAllAccounts = () => {
    const merged_array = Object.keys(availableAccounts)
    accountIds.forEach(a => {
      if (merged_array.indexOf(a) === -1) {
        merged_array.push(a)
      }
    })
    handleAccountIdsChange(merged_array)
  }

  const handleClearAllAccounts = () => {
    setAccountsSearchInput('')
    const cleared_array = []
    accountIds.forEach(a => {
      if (Object.keys(availableAccounts).indexOf(a) === -1) {
        cleared_array.push(a)
      }
    })
    handleAccountIdsChange(cleared_array)
  }

  const handleProjectIdsChange = value => {
    if (areAllAvailableSelected(value, availableProjects)) {
      setProjectsSearchInput('')
    }

    setProjectIds(value)
  }

  const handleSelectAllProjects = () => {
    const merged_array = Object.keys(availableProjects)
    projectIds.forEach(a => {
      if (merged_array.indexOf(a) === -1) {
        merged_array.push(a)
      }
    })
    handleProjectIdsChange(merged_array)
  }

  const handleClearAllProjects = () => {
    setProjectsSearchInput('')
    handleProjectIdsChange([])
  }

  const handleFieldNameChange = value => {
    const moduleFieldName = value.split(':')
    setModuleName(moduleFieldName[0])
    setFieldName(moduleFieldName[1])
  }

  const handleHintChange = e => {
    setHint(e.target.value)
  }

  const handleSelectedRecordChange = keys => {
    const recordIdsByFieldValues = []
    const valueCounts = []
    keys.forEach(key => {
      recordIdsByFieldValues.push({
        oldValue: key.value,
        recordAndProjectIds: key.recordAndProjectIds
      })
      valueCounts.push({
        value: key.value,
        count: key.count
      })
    })

    // Sort values by count (descending)
    valueCounts.sort((a, b) => b.count - a.count)

    AddBlankOptionIfRequired(keys, valueCounts)

    setSelectedRecordIds(recordIdsByFieldValues)
    setSelectedValues(valueCounts)
  }

  const AddBlankOptionIfRequired = (keys, options) => {
    if (keys.some(key => key.value.trim() === '')) {
      options.unshift({
        value: null,
        count: null
      })
    }
  }

  const handleUpdateRecordsSelectionChange = value => {
    setUpdateRecordsValue(value)
    if (value !== '') {
      const tempUpdateRecordIds = selectedRecordIds.filter(item => {
        return item.oldValue !== value
      })
      if (tempUpdateRecordIds.length > 0) {
        setSubmitEnabled(true)
      }
      setUpdateRecords(tempUpdateRecordIds)
    } else {
      setSubmitEnabled(false)
      setUpdateRecords([])
    }
  }

  const showUpdateRecords = () => {
    setShowUpdateRecordsModal(true)
  }

  const api = useApi()

  const handleUpdateRecords = async () => {
    // Disable submit and cancel buttons to prevent double submission
    setSubmitEnabled(false)
    setProcessing(true)

    await api.setRecordsFieldValues(
      updateRecords,
      tableProps.moduleName,
      updateRecordsValue,
      tableProps.fieldName
    )
    handleSummaryModalClose()
    handleUpdateRecordsModalClose()
    setProcessing(false)
    handleRunQuery()
    setLastUpdatedValue(updateRecordsValue)
  }

  const handleUpdateRecordsModalClose = () => {
    setShowUpdateRecordsModal(false)
    handleUpdateRecordsSelectionChange('')
  }

  const handleSummaryModalClose = () => {
    setShowSummaryModal(false)
  }

  if (lastUpdatedValue) {
    const scrollToValue = () => {
      const elem = document.querySelector(
        `[data-row-key*="${lastUpdatedValue}"]`
      )
      if (!elem) {
        return
      }

      const tableTop = document
        .querySelector('.ant-table-body')
        .getBoundingClientRect().top
      const elemTop = elem.getBoundingClientRect().top
      document
        .getElementsByClassName('ant-table-body')[0]
        .scrollTo(0, elemTop - tableTop)
      setLastUpdatedValue()
      clearInterval(stvInterval)
    }
    const stvInterval = setInterval(() => {
      scrollToValue()
    }, 500)
  }

  return (
    <Layout>
      <div>
        <div className="filter-section">
          <ResponsiveWrapper>
            <div id="filterBar" className="filter-bar">
              <FormItemWrapper style={{ flexGrow: 1 }}>
                <label>Created at</label>
                <RangePicker
                  showTime
                  minuteStep={5}
                  id="filterBarCreatedAtRange"
                  defaultValue={[
                    moment().subtract(1, 'year').startOf('day'),
                    moment().endOf('day')
                  ]}
                  format="DD/MM/YYYY HH:mm"
                  presets={[
                    {
                      label: 'Today',
                      value: [moment().startOf('day'), moment().endOf('day')]
                    },
                    {
                      label: 'Last 30 days',
                      value: [
                        moment().subtract(30, 'day').startOf('day'),
                        moment().endOf('day')
                      ]
                    },
                    {
                      label: 'All time',
                      value: [
                        moment('2000-01-01').startOf('day'),
                        moment().endOf('day')
                      ]
                    }
                  ]}
                  onChange={onDateRangeChange}
                  className="filter-large"
                />
              </FormItemWrapper>
              <FormItemWrapper style={{ flexGrow: 1 }}>
                <label>Accounts</label>
                <Select
                  mode="multiple"
                  allowClear
                  placeholder="Please select (optional)"
                  onChange={handleAccountIdsChange}
                  value={accountIds.sort((keyA, keyB) =>
                    accounts[keyA].name.localeCompare(accounts[keyB].name)
                  )}
                  className="scrollable-select filter-large"
                  maxTagCount="responsive"
                  searchValue={accountsSearchInput}
                  filterOption={() => true}
                  onSearch={value => {
                    setAccountsSearchInput(value)
                  }}
                  onOpenChange={open => {
                    if (!open) {
                      setAccountsSearchInput('')
                    }
                  }}
                  popupRender={menu => (
                    <div>
                      {menu}
                      <Divider style={{ margin: '4px 0' }} />{' '}
                      {!areAllAvailableSelected(
                        accountIds,
                        availableAccounts
                      ) && (
                        <Button
                          type="primary"
                          block
                          onClick={handleSelectAllAccounts}
                          style={{
                            margin: '6px 12px',
                            width: 'calc(100% - 24px)'
                          }}
                        >
                          Select All
                        </Button>
                      )}
                      {areAllAvailableSelected(
                        accountIds,
                        availableAccounts
                      ) && (
                        <Button
                          type="primary"
                          block
                          onClick={handleClearAllAccounts}
                          style={{
                            margin: '6px 12px',
                            width: 'calc(100% - 24px)'
                          }}
                        >
                          Clear All
                        </Button>
                      )}
                    </div>
                  )}
                >
                  {Object.keys(availableAccounts)
                    .sort((keyA, keyB) =>
                      accounts[keyA].name.localeCompare(accounts[keyB].name)
                    )
                    .map(k => (
                      <Select.Option key={k}>{accounts[k].name}</Select.Option>
                    ))}
                </Select>
              </FormItemWrapper>
              <FormItemWrapper style={{ flexGrow: 1 }}>
                <label htmlFor="filterBarProjects">Projects</label>
                <Select
                  id="filterBarProjects"
                  mode="multiple"
                  allowClear
                  placeholder="Please select (optional)"
                  onChange={handleProjectIdsChange}
                  value={projectIds.sort((keyA, keyB) =>
                    projects[keyA].localeCompare(projects[keyB])
                  )}
                  className="scrollable-select filter-large"
                  maxTagCount="responsive"
                  searchValue={projectsSearchInput}
                  filterOption={() => true}
                  onSearch={value => {
                    setProjectsSearchInput(value)
                  }}
                  onOpenChange={open => {
                    if (!open) {
                      setProjectsSearchInput('')
                    }
                  }}
                  popupRender={menu => (
                    <div>
                      {menu}
                      <Divider style={{ margin: '4px 0' }} />{' '}
                      {!areAllAvailableSelected(
                        projectIds,
                        availableProjects
                      ) && (
                        <Button
                          type="primary"
                          block
                          onClick={handleSelectAllProjects}
                          style={{
                            margin: '6px 12px',
                            width: 'calc(100% - 24px)'
                          }}
                        >
                          Select All
                        </Button>
                      )}
                      {areAllAvailableSelected(
                        projectIds,
                        availableProjects
                      ) && (
                        <Button
                          type="primary"
                          block
                          onClick={handleClearAllProjects}
                          style={{
                            margin: '6px 12px',
                            width: 'calc(100% - 24px)'
                          }}
                        >
                          Clear All
                        </Button>
                      )}
                    </div>
                  )}
                >
                  {Object.keys(availableProjects)
                    .sort((keyA, keyB) =>
                      projects[keyA].localeCompare(projects[keyB])
                    )
                    .map(k => (
                      <Select.Option key={k}>{projects[k]}</Select.Option>
                    ))}
                </Select>
              </FormItemWrapper>
              <FormItemWrapper>
                <label>Field name</label>
                <Select
                  placeholder="Please select"
                  onChange={handleFieldNameChange}
                  options={updateableFilterOptions()}
                  className="filter-medium"
                />
              </FormItemWrapper>
            </div>
            <div id="filterBar2" className="filter-bar">
              <FormItemWrapper>
                <label>Field value contains</label>
                <Input
                  allowClear
                  placeholder="(optional)"
                  onChange={handleHintChange}
                  className="filter-large"
                />
              </FormItemWrapper>
              <FormItemWrapper>
                <div>&nbsp;</div>
                <Button
                  type="primary"
                  icon={<Refresh />}
                  onClick={handleRunNewQuery}
                  disabled={!fieldName}
                >
                  Apply filters
                </Button>
              </FormItemWrapper>
            </div>
          </ResponsiveWrapper>
        </div>
        <ResponsiveWrapper style={{ margin: '1.5rem auto' }}>
          {tableProps.fieldName && tableProps.moduleName && (
            <>
              <Space direction="horizontal" id="action-bar">
                <div className="total-records" id="totalRecordsCount">
                  Selected:{' '}
                  <span style={{ fontWeight: '700' }}>
                    {selectedValues.reduce(
                      (partialSum, a) => partialSum + a.count,
                      0
                    )}{' '}
                    records
                  </span>
                </div>
                <Button
                  type="primary"
                  style={{ marginLeft: '0.5rem' }}
                  onClick={showUpdateRecords}
                  disabled={selectedValues.length < 1}
                >
                  Update records
                </Button>
              </Space>

              <GroupUpdateTable
                tableProps={tableProps}
                handleSelectionChange={handleSelectedRecordChange}
                addSpanToWhitespace={addSpanToWhitespace}
              />

              <UpdateRecordsModal
                visible={showUpdateRecordsModal}
                onOk={() => {
                  setShowSummaryModal(true)
                }}
                onCancel={handleUpdateRecordsModalClose}
                submitEnabled={submitEnabled}
                updateRecords={updateRecords}
                tableProps={tableProps}
                updateRecordsValue={updateRecordsValue}
                handleUpdateRecordsSelectionChange={
                  handleUpdateRecordsSelectionChange
                }
                selectedValues={selectedValues}
              />

              <UpdateRecordsSummaryModal
                visible={showSummaryModal}
                onOk={handleUpdateRecords}
                onCancel={handleSummaryModalClose}
                processing={processing}
                submitEnabled={submitEnabled}
                updateRecords={updateRecords}
                tableProps={tableProps}
                updateRecordsValue={updateRecordsValue}
              />
            </>
          )}
        </ResponsiveWrapper>
      </div>
    </Layout>
  )
}
